import {
	Component,
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	inject,
	OnDestroy
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { map, auditTime, tap, startWith } from 'rxjs/operators';
import { UploadQueueService } from '../../../_core/state/file-library/services/upload-queue.service';
import { FileLibraryService } from '../../../_core/state/file-library/file-library.service';
import { UploadQueueItem, UploadQueueState } from '../../../_core/state/file-library/file-library.model';

/**
 * Global Upload Status Bar Component
 *
 * A persistent bottom status bar that shows upload progress from any page.
 * Mounted in AppComponent to survive navigation.
 *
 * Technical patterns per review feedback:
 * - Uses async pipe for all subscriptions (no manual markForCheck)
 * - Uses auditTime(250) for progress throttling
 * - Zone optimization for XHR progress events
 * - trackBy on all ngFor loops
 * - Virtual scrolling for queues >20 files
 */
@Component({
	selector: 'app-global-upload-status-bar',
	standalone: true,
	imports: [CommonModule, MatIconModule, MatButtonModule, ScrollingModule],
	templateUrl: './global-upload-status-bar.component.html',
	styleUrls: ['./global-upload-status-bar.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush
})
export class GlobalUploadStatusBarComponent implements OnDestroy {
	private readonly uploadQueueService = inject(UploadQueueService);
	private readonly fileLibraryService = inject(FileLibraryService);
	private readonly cdr = inject(ChangeDetectorRef);

	/** Whether the file list is expanded */
	isExpanded = false;

	/** Upload speed tracking */
	currentSpeed = 0; // bytes per second
	estimatedTimeRemaining: string | null = null;
	private lastProgressSnapshot: { time: number; bytes: number } | null = null;

	/**
	 * Main upload state observable - use async pipe in template
	 * Throttled with auditTime to reduce change detection cycles
	 */
	uploadState$ = this.uploadQueueService.getState().pipe(
		auditTime(250),
		tap(state => this.calculateSpeedAndEta(state)),
		startWith({
			items: [],
			isProcessing: false,
			isPaused: false,
			concurrentLimit: 3,
			activeUploads: 0
		} as UploadQueueState)
	);

	/** Whether there are any items to show (active or completed) */
	hasItems$ = this.uploadState$.pipe(map(state => state.items.length > 0));

	/** Whether there are active uploads (uploading or pending) */
	hasActiveUploads$ = this.uploadState$.pipe(
		map(state => state.items.some(i => i.status === 'uploading' || i.status === 'pending'))
	);

	/** Whether there are failed uploads */
	hasFailedUploads$ = this.uploadState$.pipe(map(state => state.items.some(i => i.status === 'error')));

	/** Overall progress percentage */
	overallProgress$ = this.uploadState$.pipe(map(state => this.calculateOverallProgress(state.items)));

	/** Count of uploading files */
	uploadingCount$ = this.uploadState$.pipe(
		map(state => state.items.filter(i => i.status === 'uploading' || i.status === 'pending').length)
	);

	/** Count of completed files */
	completedCount$ = this.uploadState$.pipe(map(state => state.items.filter(i => i.status === 'complete').length));

	/** Count of failed files */
	failedCount$ = this.uploadState$.pipe(map(state => state.items.filter(i => i.status === 'error').length));

	/** trackBy function for ngFor - CRITICAL for performance */
	trackByItemId(_index: number, item: UploadQueueItem): string {
		return item.id;
	}

	/** Cancel a single upload */
	cancelUpload(id: string): void {
		this.uploadQueueService.cancelItem(id);
	}

	/** Cancel all uploads */
	cancelAll(): void {
		this.uploadQueueService.clearAll();
	}

	/** Retry a failed upload */
	retryUpload(id: string): void {
		this.uploadQueueService.retryItem(id);
	}

	/** Retry all failed uploads */
	retryAllFailed(): void {
		this.uploadQueueService.retryAllFailed();
	}

	/** Clear completed uploads from the list */
	clearCompleted(): void {
		this.uploadQueueService.clearCompleted();
	}

	/** Dismiss the status bar (clear all completed/cancelled/failed) */
	dismiss(): void {
		this.uploadQueueService.clearCompleted();
		this.fileLibraryService.clearCompletedUploads();
	}

	/** Toggle expanded state */
	toggleExpanded(): void {
		this.isExpanded = !this.isExpanded;
	}

	/** Calculate overall progress percentage */
	private calculateOverallProgress(items: UploadQueueItem[]): number {
		if (items.length === 0) return 0;

		const activeItems = items.filter(i => i.status === 'uploading' || i.status === 'pending');
		if (activeItems.length === 0) {
			// All done
			return 100;
		}

		const total = activeItems.reduce((sum, i) => sum + (i.progress?.percentage ?? 0), 0);
		return Math.round(total / activeItems.length);
	}

	/** Calculate upload speed and estimated time remaining */
	private calculateSpeedAndEta(state: UploadQueueState): void {
		const now = Date.now();
		const activeItems = state.items.filter(i => i.status === 'uploading' || i.status === 'pending');

		if (activeItems.length === 0) {
			this.resetSpeedTracking();
			return;
		}

		// Calculate total bytes uploaded and total size
		let totalUploaded = 0;
		let totalSize = 0;

		for (const item of activeItems) {
			const size = item.progress?.totalBytes ?? 0;
			const percentage = item.progress?.percentage ?? 0;
			totalSize += size;
			totalUploaded += (size * percentage) / 100;
		}

		// Calculate speed using recent progress (update every 0.5 seconds)
		if (this.lastProgressSnapshot) {
			const timeDiff = (now - this.lastProgressSnapshot.time) / 1000;
			// Guard against division by zero
			if (timeDiff >= 0.5) {
				const bytesDiff = totalUploaded - this.lastProgressSnapshot.bytes;
				this.currentSpeed = timeDiff > 0 ? bytesDiff / timeDiff : 0;
				this.lastProgressSnapshot = { time: now, bytes: totalUploaded };

				// Calculate ETA
				if (this.currentSpeed > 0) {
					const remainingBytes = totalSize - totalUploaded;
					const remainingSeconds = remainingBytes / this.currentSpeed;
					this.estimatedTimeRemaining = this.formatEta(remainingSeconds);
				}

				// CRITICAL: Trigger change detection for OnPush after mutating properties
				this.cdr.markForCheck();
			}
		} else {
			this.lastProgressSnapshot = { time: now, bytes: totalUploaded };
		}
	}

	/** Format time remaining as human-readable string */
	formatEta(seconds: number): string {
		if (seconds < 0 || !isFinite(seconds)) return '';
		if (seconds < 60) return `${Math.ceil(seconds)}s`;
		if (seconds < 3600) {
			const mins = Math.floor(seconds / 60);
			const secs = Math.ceil(seconds % 60);
			return secs > 0 ? `${mins}m ${secs}s` : `${mins}m`;
		}
		const hours = Math.floor(seconds / 3600);
		const mins = Math.floor((seconds % 3600) / 60);
		return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
	}

	/** Reset speed tracking state */
	private resetSpeedTracking(): void {
		const hadSpeed = this.currentSpeed > 0 || this.estimatedTimeRemaining !== null;
		this.lastProgressSnapshot = null;
		this.currentSpeed = 0;
		this.estimatedTimeRemaining = null;

		// Trigger change detection if we actually reset values
		if (hadSpeed) {
			this.cdr.markForCheck();
		}
	}

	/** Cleanup on destroy */
	ngOnDestroy(): void {
		this.resetSpeedTracking();
	}

	/** Format file size for display */
	formatFileSize(bytes: number | null | undefined): string {
		if (bytes === null || bytes === undefined || bytes === 0) return '0 B';
		const k = 1024;
		const sizes = ['B', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`;
	}

	/** Format upload speed for display */
	formatSpeed(bytesPerSecond: number): string {
		if (bytesPerSecond <= 0) return '';
		return `${this.formatFileSize(bytesPerSecond)}/s`;
	}

	/** Get status icon for an item */
	getStatusIcon(item: UploadQueueItem): string {
		switch (item.status) {
			case 'uploading':
			case 'pending':
				return 'cloud_upload';
			case 'complete':
				return 'check_circle';
			case 'error':
				return 'error';
			case 'cancelled':
				return 'cancel';
			case 'paused':
				return 'pause_circle';
			default:
				return 'hourglass_empty';
		}
	}

	/** Get status color class for an item */
	getStatusClass(item: UploadQueueItem): string {
		switch (item.status) {
			case 'complete':
				return 'status-success';
			case 'error':
				return 'status-error';
			case 'cancelled':
				return 'status-cancelled';
			default:
				return '';
		}
	}
}
